import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { motion } from 'framer-motion';
import { ArrowLeft, Star, MapPin, Phone, Video, MessageCircle, CheckCircle, Award, Calendar, Clock, Users, Shield, CreditCard, Smartphone, Building2, Mail, Linkedin, Facebook, Instagram, GraduationCap, Scale, Zap, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { bookingService } from '../services/bookingService';
import { chatService } from '../services/firebaseService';

const LawyerDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { language, setLawyers, user } = useApp();
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingStep, setBookingStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedConsultationTypes, setSelectedConsultationTypes] = useState([]);
  const [selectedFileReadingCharges, setSelectedFileReadingCharges] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLawyerBooked, setIsLawyerBooked] = useState(false);
  const [existingBooking, setExistingBooking] = useState(null);
  const [showRatingReminder, setShowRatingReminder] = useState(false);
  const [recentlyCompletedBooking, setRecentlyCompletedBooking] = useState(null);

  // Function to fetch lawyer data from Firestore
  const fetchLawyerFromFirestore = async () => {
    try {
      console.log('🔍 Fetching lawyer data from Firestore for ID:', id);
      const { db } = await import('../config/firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      
      const lawyerDoc = await getDoc(doc(db, 'lawyers', id));
      if (lawyerDoc.exists()) {
        const firestoreData = lawyerDoc.data();
        console.log('✅ Found lawyer in Firestore:', firestoreData);
        console.log('🔍 Firestore Bio:', firestoreData.bio);
        console.log('🔍 Firestore Education:', firestoreData.education);
        console.log('🔍 Firestore Languages:', firestoreData.languages);
        console.log('🔍 Firestore Experiences:', firestoreData.experiences);
        return firestoreData;
      } else {
        console.log('❌ Lawyer not found in Firestore');
        return null;
      }
    } catch (error) {
      console.error('Error fetching lawyer from Firestore:', error);
      return null;
    }
  };

  const handleBack = () => {
    navigate('/find-lawyer');
  };





  const translations = {
    english: {
      backToLawyers: 'Back to Lawyers',
      bookConsultation: 'Book Consultation',
      consultationFee: 'Consultation Fee',
      yearsExperience: 'years experience',
      successRate: 'Success Rate',
      accuracyScore: 'Accuracy Score',
      location: 'Location',
      languages: 'Languages',
      caseTypes: 'Case Types Handled',
      reviews: 'Reviews',
      rating: 'Rating',
      about: 'About',
      experience: 'Experience',
      education: 'Education',
      specializations: 'Specializations',
      consultationTypes: 'Consultation Types',
      call: 'Call',
      chat: 'Chat',
      videoCall: 'Video Call',
      selectDate: 'Select Date & Time',
      consultationType: 'Consultation Type',
      paymentMethod: 'Payment Method',
      payAndBook: 'Pay & Book',
      confirmation: 'Booking Confirmed!',
      confirmationMessage: 'Your consultation has been successfully booked. You will receive a confirmation email shortly.',
      backToHome: 'View My Bookings',
      selectDateAndTime: 'Select Date & Time',
      selectConsultationType: 'Select Consultation Type',
      creditCard: 'Credit Card',
      debitCard: 'Debit Card',
      upi: 'UPI',
      netBanking: 'Net Banking',
      totalAmount: 'Total Amount',
      bookingFee: 'Booking Fee',
      serviceCharge: 'Service Charge',
      total: 'Total',
      next: 'Next',
      confirm: 'Confirm',
      cancel: 'Cancel',
      professionalBio: 'Professional Bio',
      stats: 'Professional Stats',
      contactInfo: 'Contact Information',
      barLicense: 'BAR License',
      socialLinks: 'Social Links'
    },
    hindi: {
      backToLawyers: 'वकीलों पर वापस जाएं',
      bookConsultation: 'परामर्श बुक करें',
      consultationFee: 'परामर्श शुल्क',
      yearsExperience: 'वर्षों का अनुभव',
      successRate: 'सफलता दर',
      accuracyScore: 'सटीकता स्कोर',
      location: 'स्थान',
      languages: 'भाषाएं',
      caseTypes: 'मामलों के प्रकार',
      reviews: 'समीक्षाएं',
      rating: 'रेटिंग',
      about: 'के बारे में',
      experience: 'अनुभव',
      education: 'शिक्षा',
      specializations: 'विशेषज्ञताएं',
      consultationTypes: 'परामर्श के प्रकार',
      call: 'कॉल',
      chat: 'चैट',
      videoCall: 'वीडियो कॉल',
      selectDate: 'दिनांक और समय चुनें',
      consultationType: 'परामर्श का प्रकार',
      paymentMethod: 'भुगतान का तरीका',
      payAndBook: 'भुगतान करें और बुक करें',
      confirmation: 'बुकिंग की पुष्टि!',
      confirmationMessage: 'आपका परामर्श सफलतापूर्वक बुक हो गया है। आपको जल्द ही एक पुष्टि ईमेल प्राप्त होगा।',
      backToHome: 'मेरी बुकिंग देखें',
      selectDateAndTime: 'दिनांक और समय चुनें',
      selectConsultationType: 'परामर्श का प्रकार चुनें',
      creditCard: 'क्रेडिट कार्ड',
      debitCard: 'डेबिट कार्ड',
      upi: 'UPI',
      netBanking: 'नेट बैंकिंग',
      totalAmount: 'कुल राशि',
      bookingFee: 'बुकिंग शुल्क',
      serviceCharge: 'सेवा शुल्क',
      total: 'कुल',
      next: 'अगला',
      confirm: 'पुष्टि करें',
      cancel: 'रद्द करें',
      professionalBio: 'व्यावसायिक परिचय',
      stats: 'व्यावसायिक आंकड़े',
      contactInfo: 'संपर्क जानकारी',
      barLicense: 'BAR लाइसेंस',
      socialLinks: 'सोशल लिंक'
    }
  };

  const t = translations[language] || translations.english;

  // Get lawyer data from location state or find from localStorage
  const [lawyer, setLawyer] = useState(null);

  // Check if user has already booked this lawyer
  useEffect(() => {
    const checkExistingBooking = async () => {
      try {
        // First check if we have booking data from navigation state
        if (location.state?.fromBooking && location.state?.existingBooking) {
          console.log('✅ Found existing booking from navigation state:', location.state.existingBooking);
          setIsLawyerBooked(true);
          setExistingBooking(location.state.existingBooking);
          return;
        }

        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const userId = currentUser.uid || currentUser.id || 'anonymous';
        
        // Check localStorage bookings
        const userBookingsKey = `userBookings_${userId}`;
        const userBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
        
        // Find existing booking with this lawyer - check multiple possible fields
        let existingBooking = userBookings.find(b => 
          b.lawyerId === id || 
          b.lawyer?.id === id || 
          b.lawyerId === lawyer?.id ||
          b.lawyer?.id === lawyer?.id ||
          b.lawyerName === lawyer?.name ||
          b.lawyerName === lawyer?.full_name ||
          b.lawyer?.name === lawyer?.name ||
          b.lawyer?.name === lawyer?.full_name
        );
        
        // If not found in localStorage, check Firestore bookings
        if (!existingBooking && userId !== 'anonymous') {
          try {
            console.log('🔍 Checking Firestore bookings for lawyer ID:', id);
            const { bookingService } = await import('../services/bookingService');
            const result = await bookingService.getClientBookings(userId);
            
            if (result.success && result.data) {
              const firestoreBooking = result.data.find(b => 
                b.lawyerId === id || 
                b.lawyer?.id === id ||
                b.lawyerName === lawyer?.name ||
                b.lawyerName === lawyer?.full_name
              );
              
              if (firestoreBooking) {
                existingBooking = firestoreBooking;
                console.log('✅ Found existing booking in Firestore:', firestoreBooking);
              }
            }
          } catch (firestoreError) {
            console.error('Error checking Firestore bookings:', firestoreError);
          }
        }
        
        console.log('🔍 Checking bookings for lawyer ID:', id);
        console.log('🔍 Available localStorage bookings:', userBookings);
        console.log('🔍 Looking for booking with lawyerId or lawyer.id matching:', id);
        
        if (existingBooking) {
          setIsLawyerBooked(true);
          setExistingBooking(existingBooking);
          console.log('✅ User has already booked this lawyer:', existingBooking);
        } else {
          setIsLawyerBooked(false);
          setExistingBooking(null);
          console.log('❌ User has not booked this lawyer yet');
        }
      } catch (error) {
        console.error('Error checking existing booking:', error);
        setIsLawyerBooked(false);
        setExistingBooking(null);
      }
    };

    // Only check if we have the lawyer data or the ID
    if (id) {
      checkExistingBooking();
    }
  }, [id, lawyer, location.state]);

  // Function to fetch lawyer rating statistics from Firestore
  const fetchLawyerRatingStats = async (lawyerId) => {
    try {
      const { ratingService } = await import('../services/firebaseService');
      const result = await ratingService.getLawyerRatingStats(lawyerId);
      
      if (result.success) {
        return result.data;
      } else {
        console.warn('Could not fetch lawyer rating stats:', result.error);
        return null;
      }
    } catch (error) {
      console.error('Error fetching lawyer rating stats:', error);
      return null;
    }
  };

  // Load lawyer data with rating statistics
  useEffect(() => {
    const loadLawyerData = async () => {
      setIsLoading(true);
      try {
        let lawyerData = null;

        // Check if data comes from location state (from FindLawyer or MyBookings)
        if (location.state?.lawyer) {
          console.log('🔍 Loading lawyer data from location state');
          lawyerData = location.state.lawyer;
          setExistingBooking(location.state.existingBooking);
        } else {
          // Try to get from localStorage
          const lawyers = JSON.parse(localStorage.getItem('lawyers') || '[]');
          lawyerData = lawyers.find(l => l.id === id || l.uid === id);
          
          if (!lawyerData) {
            // Try to get from userBookings
            const userBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
            const bookingWithLawyer = userBookings.find(b => b.lawyerId === id || b.lawyer?.id === id);
            if (bookingWithLawyer?.lawyer) {
              lawyerData = bookingWithLawyer.lawyer;
            }
          }
        }

        if (lawyerData) {
          // Fetch real-time rating statistics from Firestore
          const ratingStats = await fetchLawyerRatingStats(id);
          if (ratingStats) {
            lawyerData = {
              ...lawyerData,
              rating: ratingStats.rating,
              totalReviews: ratingStats.totalReviews,
              ratingDistribution: ratingStats.ratingDistribution
            };
          }

          // Final step: If we still don't have complete data, fetch from Firestore
          const loadCompleteData = async () => {
            if (!lawyerData.bio || !lawyerData.education || !lawyerData.languages || !lawyerData.experiences) {
              console.log('🔍 Incomplete lawyer data, fetching from Firestore...');
              const firestoreData = await fetchLawyerFromFirestore();
              if (firestoreData) {
                // Merge Firestore data with existing data
                lawyerData = { ...lawyerData, ...firestoreData };
                console.log('✅ Merged Firestore data with existing data');
              }
            }

            // Final debugging and data setting
            console.log('🔍 FINAL - Setting lawyer data:', lawyerData);
            console.log('🔍 FINAL - Bio:', lawyerData?.bio);
            console.log('🔍 FINAL - Education:', lawyerData?.education);
            console.log('🔍 FINAL - Languages:', lawyerData?.languages);
            console.log('🔍 FINAL - Experiences:', lawyerData?.experiences);

            setLawyer(lawyerData);
            setIsLoading(false);
          };

          loadCompleteData();
        } else {
          // No lawyer found - try to fetch from Firestore directly
          console.log('🔍 No lawyer found in location state or localStorage, trying Firestore...');
          const firestoreData = await fetchLawyerFromFirestore();
          if (firestoreData) {
            console.log('✅ Found lawyer in Firestore:', firestoreData);
            setLawyer(firestoreData);
            setIsLoading(false);
          } else {
            console.log('❌ No lawyer found with ID:', id);
            // Instead of showing error, try to load from all lawyers collection
            console.log('🔍 Trying to find lawyer in all lawyers collection...');
            try {
              const { collection, getDocs, query, where } = await import('firebase/firestore');
              const { db } = await import('../config/firebase');
              
              const lawyersRef = collection(db, 'lawyers');
              const q = query(lawyersRef, where('__name__', '==', id));
              const querySnapshot = await getDocs(q);
              
              if (!querySnapshot.empty) {
                const lawyerDoc = querySnapshot.docs[0];
                const lawyerData = { id: lawyerDoc.id, ...lawyerDoc.data() };
                console.log('✅ Found lawyer in all lawyers collection:', lawyerData);
                setLawyer(lawyerData);
                setIsLoading(false);
              } else {
                console.log('❌ Lawyer not found in any collection');
          setLawyer(null);
          setIsLoading(false);
                // Don't show error toast, just set lawyer to null
              }
            } catch (error) {
              console.error('❌ Error searching for lawyer:', error);
              setLawyer(null);
              setIsLoading(false);
              // Don't show error toast
            }
          }
        }
      } catch (error) {
        console.error('Error loading lawyer data:', error);
        setLawyer(null);
        setIsLoading(false);
      }
    };

    loadLawyerData();
  }, [id, location.state]);

  useEffect(() => {
    let lawyerData = location.state?.lawyer;
    console.log('🔍 LawyerDetail: Initial lawyer data from location state:', lawyerData);
    console.log('🔍 LawyerDetail: Full location state:', location.state);
    console.log('🔍 LawyerDetail: From booking flag:', location.state?.fromBooking);
    console.log('🔍 LawyerDetail: Existing booking data:', location.state?.existingBooking);
    
    // Debug: Log specific sections data
    if (lawyerData) {
      console.log('🔍 DEBUG - Professional Bio:', lawyerData.bio);
      console.log('🔍 DEBUG - Education:', lawyerData.education);
      console.log('🔍 DEBUG - Languages:', lawyerData.languages);
      console.log('🔍 DEBUG - Experiences:', lawyerData.experiences);
      console.log('🔍 DEBUG - Degree:', lawyerData.degree);
      console.log('🔍 DEBUG - Year of Passing:', lawyerData.yearOfPassing);
    }
    
    // Enhance lawyer data if it comes from location state (from MyBookings)
    if (lawyerData && location.state?.fromBooking) {
      console.log('🔧 Enhancing lawyer data from location state...');
      console.log('🔍 Original lawyer data:', lawyerData);
      const bookingData = location.state?.bookingData || location.state?.existingBooking;
      console.log('🔍 Booking data:', bookingData);
      
      // Ensure BAR License information is present
      if (!lawyerData.bar_council_number && !lawyerData.barLicense) {
        lawyerData.bar_council_number = bookingData?.barLicense || 'BAR123456';
        lawyerData.barLicense = bookingData?.barLicense || 'BAR123456';
      }
      
      // Ensure Chamber Address is present
      if (!lawyerData.chamber_address && !lawyerData.chamberAddress && !lawyerData.address) {
        lawyerData.chamber_address = bookingData?.chamberAddress || 'Chamber Address, City, State';
        lawyerData.chamberAddress = bookingData?.chamberAddress || 'Chamber Address, City, State';
        lawyerData.address = bookingData?.chamberAddress || 'Chamber Address, City, State';
      }
      
      // Ensure social media links are present - only set email if available, leave others empty
      if (!lawyerData.social) {
        lawyerData.social = {
          email: bookingData?.lawyerEmail || lawyerData.email || '',
          linkedin: '',
          facebook: '',
          instagram: '',
          twitter: '',
          website: '',
          whatsapp: ''
        };
      }
      
      // Ensure fees are properly structured
      if (!lawyerData.fees) {
        lawyerData.fees = {
          text: bookingData?.textFee || 1500,
          call: bookingData?.callFee || 2500,
          video: bookingData?.videoFee || 3000,
          in_person: bookingData?.inPersonFee || 3500,
          emergency: bookingData?.emergencyFee || 5000,
          consultation: bookingData?.consultationFees || 2500
        };
      }
      
      console.log('✅ Enhanced lawyer data:', {
        barLicense: lawyerData.bar_council_number || lawyerData.barLicense,
        chamberAddress: lawyerData.chamber_address || lawyerData.chamberAddress || lawyerData.address,
        consultationFee: lawyerData.fees?.consultation,
        social: lawyerData.social
      });
    }
    
    // If no lawyer data in state, try to find from localStorage
    if (!lawyerData) {
      try {
        const storedLawyers = JSON.parse(localStorage.getItem('lawyers') || '[]');
        lawyerData = storedLawyers.find(l => l.id === id);
        console.log('🔍 Found lawyer in localStorage:', lawyerData);
      } catch (error) {
        console.error('Error loading lawyer data from localStorage:', error);
      }
    }
    
    // If still no lawyer data, try to load from dummy data as fallback
    if (!lawyerData) {
      console.log('No lawyer data found from available sources');
    }
    
    // If still no lawyer data, try to find from user-specific bookings
    if (!lawyerData) {
      try {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const userId = currentUser.uid || currentUser.id || 'anonymous';
        const userBookingsKey = `userBookings_${userId}`;
        const userBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
        
        // Find booking with this lawyer ID
        const bookingWithLawyer = userBookings.find(b => b.lawyerId === id);
        if (bookingWithLawyer) {
          console.log('Found lawyer data from user booking:', bookingWithLawyer);
          // Create comprehensive lawyer data from booking
          lawyerData = {
            id: bookingWithLawyer.lawyerId,
            full_name: bookingWithLawyer.lawyerName,
            name: bookingWithLawyer.lawyerName,
            specialization: bookingWithLawyer.specialization || 'General Law',
            location: bookingWithLawyer.location || 'Location not specified',
            bar_council_state: bookingWithLawyer.location || 'Location not specified',
            rating: bookingWithLawyer.rating || 4.5,
            experience_years: bookingWithLawyer.experience || 5,
            profile_pic: bookingWithLawyer.profilePic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
            email: bookingWithLawyer.lawyerEmail || 'lawyer@example.com',
            bio: bookingWithLawyer.lawyer?.bio || 'Experienced lawyer with expertise in various legal matters.',
            isVerified: bookingWithLawyer.lawyer?.isVerified || true,
            isOnline: bookingWithLawyer.lawyer?.isOnline || true,
            availability: bookingWithLawyer.lawyer?.availability || 'online',
            // Add BAR License information
            bar_council_number: bookingWithLawyer.barLicense || bookingWithLawyer.lawyer?.barLicense || 'BAR123456',
            barLicense: bookingWithLawyer.barLicense || bookingWithLawyer.lawyer?.barLicense || 'BAR123456',
            // Add Chamber Address
            chamber_address: bookingWithLawyer.chamberAddress || bookingWithLawyer.lawyer?.chamberAddress || 'Chamber Address, City, State',
            chamberAddress: bookingWithLawyer.chamberAddress || bookingWithLawyer.lawyer?.chamberAddress || 'Chamber Address, City, State',
            address: bookingWithLawyer.chamberAddress || bookingWithLawyer.lawyer?.chamberAddress || 'Chamber Address, City, State',
            fees: {
              text: bookingWithLawyer.textFee || 1500,
              call: bookingWithLawyer.callFee || 2500,
              video: bookingWithLawyer.videoFee || 3000,
              in_person: bookingWithLawyer.inPersonFee || 3500,
              emergency: bookingWithLawyer.emergencyFee || 5000,
              consultation: bookingWithLawyer.consultationFees || 2500
            },
            emergency_available: bookingWithLawyer.isEmergency || false,
            consultation_types: bookingWithLawyer.consultationTypes || ['text', 'call', 'video'],
            languages: bookingWithLawyer.lawyer?.languages || ['English', 'Hindi'],
            education: bookingWithLawyer.lawyer?.education || [
              {
                qualification: 'LL.B. (Bachelor of Laws)',
                degreeCollege: 'Law College',
                year: '2015'
              }
            ],
            degree: bookingWithLawyer.lawyer?.degree || 'LL.B. (Bachelor of Laws)',
            yearOfPassing: bookingWithLawyer.lawyer?.yearOfPassing || '2015',
            totalReviews: bookingWithLawyer.lawyer?.totalReviews || 50,
            reviews: bookingWithLawyer.lawyer?.reviews || 50,
            successRate: bookingWithLawyer.lawyer?.successRate || 95,
            accuracyScore: bookingWithLawyer.lawyer?.accuracyScore || 92,
            achievements: bookingWithLawyer.lawyer?.achievements || [
              'Best Lawyer Award 2020',
              '10+ Years of Experience',
              '500+ Cases Handled Successfully'
            ],
            experiences: bookingWithLawyer.lawyer?.experiences || [
              {
                id: 1,
                subPracticeArea: 'Civil Law',
                casesHandled: 150,
                yearsOfPractice: 5
              }
            ],
            workingHours: bookingWithLawyer.lawyer?.workingHours || {
              monday: { start: '09:00 AM', end: '06:00 PM' },
              tuesday: { start: '09:00 AM', end: '06:00 PM' },
              wednesday: { start: '09:00 AM', end: '06:00 PM' },
              thursday: { start: '09:00 AM', end: '06:00 PM' },
              friday: { start: '09:00 AM', end: '06:00 PM' }
            },
            available_days: bookingWithLawyer.lawyer?.available_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            time_slots: bookingWithLawyer.lawyer?.time_slots || {
              monday: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
              tuesday: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
              wednesday: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
              thursday: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
              friday: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM']
            },
            file_charges: bookingWithLawyer.lawyer?.file_charges || [
              { fromPage: 1, toPage: 10, amount: 500 },
              { fromPage: 11, toPage: 25, amount: 1000 },
              { fromPage: 26, toPage: 50, amount: 1500 }
            ],
            social: {
              email: bookingWithLawyer.lawyerEmail || bookingWithLawyer.lawyer?.social?.email || 'lawyer@example.com',
              linkedin: bookingWithLawyer.lawyer?.social?.linkedin || 'https://linkedin.com/in/lawyer',
              facebook: bookingWithLawyer.lawyer?.social?.facebook || 'https://facebook.com/lawyer',
              instagram: bookingWithLawyer.lawyer?.social?.instagram || 'https://instagram.com/lawyer',
              twitter: bookingWithLawyer.lawyer?.social?.twitter || 'https://twitter.com/lawyer',
              website: bookingWithLawyer.lawyer?.social?.website || 'https://lawyer-website.com',
              whatsapp: bookingWithLawyer.lawyer?.social?.whatsapp || '+919876543210'
            }
          };
        }
      } catch (error) {
        console.error('Error loading lawyer data from user bookings:', error);
      }
    }
    
                   if (lawyerData) {
            // Final step: If we still don't have complete data, fetch from Firestore
            const loadCompleteData = async () => {
              if (!lawyerData.bio || !lawyerData.education || !lawyerData.languages || !lawyerData.experiences) {
                console.log('🔍 Incomplete lawyer data, fetching from Firestore...');
                const firestoreData = await fetchLawyerFromFirestore();
                if (firestoreData) {
                  // Merge Firestore data with existing data
                  lawyerData = { ...lawyerData, ...firestoreData };
                  console.log('✅ Merged Firestore data with existing data');
                }
              }
              
              // Final debugging and data setting
              console.log('🔍 FINAL - Setting lawyer data:', lawyerData);
              console.log('🔍 FINAL - Bio:', lawyerData?.bio);
              console.log('🔍 FINAL - Education:', lawyerData?.education);
              console.log('🔍 FINAL - Languages:', lawyerData?.languages);
              console.log('🔍 FINAL - Experiences:', lawyerData?.experiences);
              
              setLawyer(lawyerData);
              setIsLoading(false);
            };
            
            loadCompleteData();
          } else {
            // No lawyer found - show error
            console.log('No lawyer found with ID:', id);
            setLawyer(null);
            toast.error('Lawyer not found. Please try again.');
            setIsLoading(false);
          }
   }, [id, location.state]);

       // Load wallet balance
    useEffect(() => {
      if (user && user.uid) {
        const balance = JSON.parse(localStorage.getItem(`client_wallet_${user.uid}`) || '0');
        setWalletBalance(balance);
      }
    }, [user]);

         // Allow multiple bookings - don't check booking status
     useEffect(() => {
       setIsLawyerBooked(false);
     }, [id]);

   // Function to refresh wallet balance
   const refreshWalletBalance = () => {
     if (user && user.uid) {
       const balance = JSON.parse(localStorage.getItem(`client_wallet_${user.uid}`) || '0');
       setWalletBalance(balance);
     }
   };

   // Function to add money to wallet (for testing)
   const addMoneyToWallet = (amount) => {
     if (user && user.uid) {
       const currentBalance = JSON.parse(localStorage.getItem(`client_wallet_${user.uid}`) || '0');
       const newBalance = currentBalance + amount;
       localStorage.setItem(`client_wallet_${user.uid}`, JSON.stringify(newBalance));
       setWalletBalance(newBalance);
       toast.success(`₹${amount.toLocaleString()} added to wallet!`);
     }
   };

  const consultationTypes = [
    ...(lawyer?.fees?.text && lawyer.fees.text > 0 ? [{
      id: 'text', 
      name: 'Text Consultation', 
      icon: <MessageCircle className="w-5 h-5" />, 
      price: `₹${lawyer.fees.text}`
    }] : []),
    ...(lawyer?.fees?.call && lawyer.fees.call > 0 ? [{
      id: 'call', 
      name: t.call, 
      icon: <Phone className="w-5 h-5" />, 
      price: `₹${lawyer.fees.call}`
    }] : []),
    ...(lawyer?.fees?.video && lawyer.fees.video > 0 ? [{
      id: 'video', 
      name: t.videoCall, 
      icon: <Video className="w-5 h-5" />, 
      price: `₹${lawyer.fees.video}`
    }] : []),
    ...(lawyer?.fees?.in_person && lawyer.fees.in_person > 0 ? [{
      id: 'inperson', 
      name: 'In Person', 
      icon: <Building2 className="w-5 h-5" />, 
      price: `₹${lawyer.fees.in_person}`
    }] : [])
  ];

     const paymentMethods = [
     { id: 'credit', name: t.creditCard, icon: <CreditCard className="w-5 h-5" /> },
     { id: 'debit', name: t.debitCard, icon: <CreditCard className="w-5 h-5" /> },
     { id: 'upi', name: t.upi, icon: <Smartphone className="w-5 h-5" /> },
     { id: 'netbanking', name: t.netBanking, icon: <Building2 className="w-5 h-5" /> },
     { 
       id: 'wallet', 
       name: `Wallet (₹${walletBalance.toLocaleString()})`, 
       icon: <Wallet className="w-5 h-5" />,
       extra: (
         <div
           onClick={(e) => {
             e.stopPropagation();
             refreshWalletBalance();
             toast.success('Wallet balance refreshed!');
           }}
           className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors cursor-pointer"
           title="Refresh balance"
         >
           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
           </svg>
         </div>
       )
     }
   ];

  // Function to get available time slots for a specific date
  const getAvailableTimeSlots = (date) => {
    console.log('getAvailableTimeSlots called with date:', date);
    console.log('Current lawyer:', lawyer);
    
    if (!date || !lawyer || !lawyer.time_slots) {
      console.log('Early return - missing data:', { date, lawyer: !!lawyer, time_slots: lawyer?.time_slots });
      // Return default time slots for testing if no lawyer data
      if (date) {
        return ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'];
      }
      return [];
    }
    
    try {
      // Get the day of the week from the selected date
      const selectedDate = new Date(date);
      
      // Check if the date is valid
      if (isNaN(selectedDate.getTime())) {
        console.error('Invalid date:', date);
        return [];
      }
      
      // Get the day name in different formats
      const dayOfWeekCapitalized = selectedDate.toLocaleDateString('en-US', { weekday: 'long' });
      const dayOfWeekLower = dayOfWeekCapitalized.toLowerCase();
      
      console.log('Looking for time slots for day:', dayOfWeekLower, 'or', dayOfWeekCapitalized);
      console.log('Available time slots object:', lawyer.time_slots);
      console.log('Available time slots keys:', Object.keys(lawyer.time_slots));
      
      // Try all possible formats
      let dayTimeSlots = null;
      
      // First try the exact format from the time_slots object
      if (lawyer.time_slots[dayOfWeekLower]) {
        dayTimeSlots = lawyer.time_slots[dayOfWeekLower];
        console.log('Found time slots using lowercase:', dayOfWeekLower, dayTimeSlots);
      } else if (lawyer.time_slots[dayOfWeekCapitalized]) {
        dayTimeSlots = lawyer.time_slots[dayOfWeekCapitalized];
        console.log('Found time slots using capitalized:', dayOfWeekCapitalized, dayTimeSlots);
      } else {
        // Try to find by matching any key that contains the day name
        const dayKeys = Object.keys(lawyer.time_slots);
        const matchingKey = dayKeys.find(key => 
          key.toLowerCase() === dayOfWeekLower || 
          key.toLowerCase() === dayOfWeekCapitalized.toLowerCase()
        );
        
        if (matchingKey) {
          dayTimeSlots = lawyer.time_slots[matchingKey];
          console.log('Found time slots using matching key:', matchingKey, dayTimeSlots);
        }
      }
      
      if (!dayTimeSlots || !Array.isArray(dayTimeSlots)) {
        console.log('No time slots found for day:', dayOfWeekLower, 'or', dayOfWeekCapitalized);
        console.log('Available keys in time_slots:', Object.keys(lawyer.time_slots));
        // Return default time slots for testing
        return ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'];
      }
      
      // Filter out any temporary or internal slots
      const filteredSlots = dayTimeSlots.filter(slot => typeof slot === 'string' && !slot.startsWith('_'));
      console.log('Filtered time slots:', filteredSlots);
      
      return filteredSlots;
    } catch (error) {
      console.error('Error getting available time slots:', error);
      // Return default time slots for testing
      return ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'];
    }
  };

  // Get time slots for the selected date
  const availableTimeSlots = getAvailableTimeSlots(selectedDate);

  // Function to get consultation fee based on type
  const getConsultationFee = (type) => {
    switch (type) {
      case 'text':
        return lawyer?.fees?.text || 1500;
      case 'call':
        return lawyer?.fees?.call || 2500;
      case 'video':
        return lawyer?.fees?.video || 3000;
      case 'inperson':
        return lawyer?.fees?.in_person || 3500;
      case 'emergency':
        return lawyer?.fees?.emergency || lawyer?.emergencyConsultationFee || 5000;
      default:
        return lawyer?.fees?.consultation || lawyer?.fee || 2500;
    }
  };

  // Function to get total consultation fees
  const getTotalConsultationFees = () => {
    return selectedConsultationTypes.reduce((total, type) => {
      return total + getConsultationFee(type);
    }, 0);
  };

  // Function to get total file reading charges
  const getTotalFileReadingCharges = () => {
    return selectedFileReadingCharges.reduce((total, charge) => {
      return total + charge.amount;
    }, 0);
  };

  // Function to get total amount
  const getTotalAmount = () => {
    const consultationFees = getTotalConsultationFees();
    const fileReadingCharges = getTotalFileReadingCharges();
    return consultationFees + fileReadingCharges;
  };

  // Function to handle consultation type selection
  const handleConsultationTypeToggle = (type) => {
    setSelectedConsultationTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Function to handle file reading charge selection
  const handleFileReadingChargeToggle = (charge) => {
    setSelectedFileReadingCharges(prev => {
      const exists = prev.find(c => c.fromPage === charge.fromPage);
      if (exists) {
        return prev.filter(c => c.fromPage !== charge.fromPage);
      } else {
        return [...prev, charge];
      }
    });
  };

     const handleBookConsultation = () => {
     // Check if lawyer has any consultation types configured
     const hasConsultationTypes = consultationTypes.length > 0 || lawyer?.emergency_available || lawyer?.emergencyConsultationAvailable;
     
     if (!hasConsultationTypes) {
       toast.error('This lawyer has not configured any consultation types yet.');
       return;
     }
     
     setShowBookingModal(true);
     setBookingStep(1);
   };

  const handleBookingStep = async (step) => {
    if (step === 3) {
      // Payment and booking confirmation step
      try {
        // Get current user from context
        const currentUser = user || { uid: 'user123', email: 'user@example.com' };
        console.log('🔍 LawyerDetail: Current user for booking:', currentUser);
        
        // Ensure we have a consistent user ID
        const userId = currentUser.id || currentUser.uid;
        console.log('🔍 LawyerDetail: Using user ID for chat:', userId);
        
        // Debug: Log lawyer data structure
        console.log('🔍 Lawyer data for booking:', {
          id: lawyer.id,
          full_name: lawyer.full_name,
          fullName: lawyer.fullName,
          name: lawyer.name,
          lawyerName: lawyer.lawyerName,
          email: lawyer.email,
          gmail: lawyer.gmail
        });

                 // Prepare booking data
         const bookingData = {
           userId: userId,
           userEmail: currentUser.email,
           lawyerId: lawyer.id,
           lawyerName: lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer',
           lawyerEmail: lawyer.email || lawyer.gmail || 'lawyer@example.com',
           specialization: lawyer.specialization || 'General Law',
           location: lawyer.bar_council_state || lawyer.location || 'Location not specified',
           rating: lawyer.rating || 0,
           experience: lawyer.experience_years || lawyer.experience || 0,
           date: selectedDate,
           time: selectedTime,
           duration: 60,
           consultationTypes: selectedConsultationTypes,
           consultationFees: getTotalConsultationFees(),
           fileReadingCharges: getTotalFileReadingCharges(),
           serviceCharge: 200,
           totalAmount: getTotalAmount() + 200,
           selectedFileReadingCharges: selectedFileReadingCharges,
           isEmergency: selectedConsultationTypes.includes('emergency'),
           profilePic: lawyer.profile_pic || lawyer.profilePic || '',
           paymentMethod: selectedPaymentMethod || 'online',
           paymentStatus: 'paid',
                       // Store complete lawyer data for future reference (ensuring no undefined values)
            lawyer: {
              id: lawyer.id || '',
              full_name: lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer',
              name: lawyer.name || lawyer.full_name || lawyer.fullName || lawyer.lawyerName || 'Unknown Lawyer',
              specialization: lawyer.specialization || 'General Law',
              location: lawyer.location || 'Location not specified',
              bar_council_state: lawyer.bar_council_state || 'Location not specified',
              bar_council_number: lawyer.bar_council_number || 'BAR123456',
              barLicense: lawyer.barLicense || 'BAR123456',
              rating: lawyer.rating || 4.5,
              average_rating: lawyer.average_rating || lawyer.rating || 4.5,
              experience_years: lawyer.experience_years || lawyer.experience || 5,
              profile_pic: lawyer.profile_pic || lawyer.profilePic || '',
              email: lawyer.email || lawyer.gmail || 'lawyer@example.com',
              bio: lawyer.bio || 'Experienced lawyer with expertise in various legal matters.',
              isVerified: lawyer.isVerified !== undefined ? lawyer.isVerified : true,
              isOnline: lawyer.isOnline !== undefined ? lawyer.isOnline : true,
              availability: lawyer.availability || 'online',
              fees: lawyer.fees || {
                text: 1500,
                call: 2500,
                video: 3000,
                in_person: 3500,
                emergency: 5000,
                consultation: 2500
              },
              emergency_available: lawyer.emergency_available !== undefined ? lawyer.emergency_available : false,
              consultation_types: lawyer.consultation_types || ['text', 'call', 'video'],
              languages: lawyer.languages || ['English', 'Hindi'],
              education: lawyer.education || [
                {
                  qualification: 'LL.B. (Bachelor of Laws)',
                  degreeCollege: 'Law College',
                  year: '2015'
                }
              ],
              degree: lawyer.degree || 'LL.B. (Bachelor of Laws)',
              yearOfPassing: lawyer.yearOfPassing || '2015',
              totalReviews: lawyer.totalReviews || lawyer.reviews || 50,
              reviews: lawyer.reviews || lawyer.totalReviews || 50,
              successRate: lawyer.successRate || 95,
              accuracyScore: lawyer.accuracyScore || 92,
              achievements: lawyer.achievements || [
                'Best Lawyer Award 2020',
                '10+ Years of Experience',
                '500+ Cases Handled Successfully'
              ],
              experiences: lawyer.experiences || [
                {
                  id: 1,
                  subPracticeArea: 'Civil Law',
                  casesHandled: 150,
                  yearsOfPractice: 5
                }
              ],
              workingHours: lawyer.workingHours || lawyer.working_hours || null,
              available_days: lawyer.available_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
              time_slots: lawyer.time_slots || {
                monday: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
                tuesday: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
                wednesday: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
                thursday: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM'],
                friday: ['09:00 AM', '10:00 AM', '11:00 AM', '02:00 PM', '03:00 PM', '04:00 PM']
              },
              file_charges: lawyer.file_charges || [
                { fromPage: 1, toPage: 10, amount: 500 },
                { fromPage: 11, toPage: 25, amount: 1000 },
                { fromPage: 26, toPage: 50, amount: 1500 }
              ],
              social: lawyer.social || {
                email: lawyer.email || lawyer.gmail || 'lawyer@example.com',
                linkedin: 'https://linkedin.com/in/lawyer',
                facebook: 'https://facebook.com/lawyer',
                instagram: 'https://instagram.com/lawyer'
              },
              contact: lawyer.contact || '+91 98765 43210',
              gmail: lawyer.gmail || lawyer.email || 'lawyer@example.com',
              chamberAddress: lawyer.chamberAddress || lawyer.chamber_address || lawyer.address || null
            }
         };

        // Create booking in database
        const bookingResult = await bookingService.createBooking(bookingData);
        
        if (bookingResult.success) {
                     // Create chat conversation
           const chatData = {
             bookingId: bookingResult.bookingId,
             participants: [userId, lawyer.id],
             clientId: userId,
             lawyerId: lawyer.id,
             lawyerName: lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer',
             lawyerProfilePic: lawyer.profile_pic || lawyer.profilePic || '',
             specialization: Array.isArray(lawyer.specialization) ? lawyer.specialization.join(', ') : lawyer.specialization || 'General Law',
             clientName: currentUser.displayName || 'Client',
             clientProfilePic: currentUser.photoURL || '',
             consultationDate: selectedDate,
             consultationTime: selectedTime,
             consultationTypes: selectedConsultationTypes,
             status: 'pending'
           };

          console.log('🔍 Creating chat with data:', chatData);
          const chatResult = await chatService.createChatRoom(chatData);
          console.log('🔍 Chat creation result:', chatResult);
          
                     // Also add to local storage for immediate access
           const existingChats = JSON.parse(localStorage.getItem('userChats') || '[]');
           const consultationTypesText = selectedConsultationTypes.map(type => 
             type === 'call' ? 'Phone Call' : 
             type === 'video' ? 'Video Call' : 
             type === 'text' ? 'Text Consultation' : 
             type === 'emergency' ? 'Emergency Consultation' : 
             'In Person Consultation'
           ).join(', ');
           
           const newChatForStorage = {
             id: chatResult.data.id,
             ...chatData,
             clientId: userId, // Ensure consistent user ID
             bookingId: bookingResult.bookingId,
             lastMessage: `🎉 Booking Confirmed! ${lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer'} - ${selectedDate} at ${selectedTime}`,
             lastMessageTime: new Date().toISOString(),
             unreadCount: 1,
             status: 'pending',
             bookingDate: selectedDate,
             bookingTime: selectedTime,
             consultationTypes: consultationTypesText,
             specialization: Array.isArray(lawyer.specialization) ? lawyer.specialization.join(', ') : lawyer.specialization || 'General Law',
             lawyer: {
               id: lawyer.id,
               name: lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer',
               profilePic: lawyer.profile_pic || lawyer.profilePic || '',
               specialization: Array.isArray(lawyer.specialization) ? lawyer.specialization.join(', ') : lawyer.specialization || 'General Law'
             }
           };
          const updatedChats = [...existingChats, newChatForStorage];
          localStorage.setItem('userChats', JSON.stringify(updatedChats));
          console.log('🔍 Added chat to localStorage:', newChatForStorage);
          
          // Dispatch event to notify chat components
          window.dispatchEvent(new CustomEvent('chatUpdated'));
          
          if (chatResult.success) {
            // Send automated booking confirmation message with detailed information
            const bookingDetails = {
              lawyerName: lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer',
              date: selectedDate,
              time: selectedTime,
              consultationTypes: selectedConsultationTypes,
              fileReadingCharges: selectedFileReadingCharges,
              bookingId: bookingResult.bookingId,
              totalAmount: getTotalAmount() + 200,
              consultationFees: getTotalConsultationFees(),
              serviceCharge: 200,
              duration: 60
            };
            
            console.log('🔍 Sending automated booking confirmation:', bookingDetails);
            await chatService.sendAutomatedMessage(
              chatResult.data.id,
              'booking_confirmed',
              bookingDetails
            );

            // Add to local storage for immediate UI update
            const newBooking = {
              id: bookingResult.bookingId,
              ...bookingData,
              status: 'pending',
              chatId: chatResult.data.id
            };

             // Add to user bookings - use user-specific key
             const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
             const userId = currentUser.uid || currentUser.id || 'anonymous';
             
             // Handle wallet payment if selected
             if (selectedPaymentMethod === 'wallet') {
               const totalAmount = getTotalAmount() + 200;
               const newBalance = walletBalance - totalAmount;
               localStorage.setItem(`client_wallet_${userId}`, JSON.stringify(newBalance));
               setWalletBalance(newBalance);
             }
             const userBookingsKey = `userBookings_${userId}`;
             const existingBookings = JSON.parse(localStorage.getItem(userBookingsKey) || '[]');
             const updatedBookings = [...existingBookings, newBooking];
             localStorage.setItem(userBookingsKey, JSON.stringify(updatedBookings));

            // Allow multiple bookings - don't remove lawyer from available list
            console.log('✅ Multiple bookings allowed - lawyer remains available for future bookings');

            // Add chat to user chats
            const newChat = {
              id: chatResult.chatId,
              bookingId: bookingResult.bookingId,
              chatRoomId: chatResult.chatId,
              profilePic: lawyer.profile_pic || lawyer.profilePic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
              lawyerName: lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer',
              specialization: Array.isArray(lawyer.specialization) ? lawyer.specialization.join(', ') : lawyer.specialization || 'General Law',
                lawyer: {
                  id: lawyer.id,
                  name: lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer',
                  full_name: lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer',
                  specialization: Array.isArray(lawyer.specialization) ? lawyer.specialization.join(', ') : lawyer.specialization || 'General Law',
                  profile_pic: lawyer.profile_pic || lawyer.profilePic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
                  profilePic: lawyer.profile_pic || lawyer.profilePic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
                  contact: lawyer.contact || '+91 98765 43210',
                  email: lawyer.email || lawyer.gmail || 'lawyer@example.com',
                  experience: `${lawyer.experience_years || lawyer.experience || 0}+ years`,
                  location: lawyer.bar_council_state || lawyer.location || 'Location not specified',
                  about: lawyer.bio || 'Experienced lawyer',
                  education: lawyer.degree || (lawyer.education && Array.isArray(lawyer.education) ? lawyer.education[0]?.qualification : null) || 'LL.B.',
                  rating: lawyer.rating || 4.5,
                  reviews: lawyer.totalReviews || lawyer.reviews || 50,
                  caseTypes: lawyer.specialization ? (Array.isArray(lawyer.specialization) ? lawyer.specialization : [lawyer.specialization]) : ['General Law'],
                  languages: lawyer.languages || ['English', 'Hindi']
                },
                lastMessage: `🎉 Booking Confirmed! ${lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer'} - ${selectedDate} at ${selectedTime}`,
                lastMessageTime: 'Just now',
                unreadCount: 1,
                status: 'pending',
                statusLabel: 'Pending',
                bookingDate: selectedDate,
                bookingTime: selectedTime,
                consultationTypes: selectedConsultationTypes.map(type => type === 'call' ? 'Phone Call' : type === 'video' ? 'Video Call' : type === 'text' ? 'Text Consultation' : type === 'emergency' ? 'Emergency Consultation' : 'In Person Consultation')
              };

            const existingChats = JSON.parse(localStorage.getItem('userChats') || '[]');
            
            // Check if a chat with this lawyer already exists
            const existingChatIndex = existingChats.findIndex(chat => 
              (chat.lawyer?.id || chat.lawyerId) === lawyer.id
            );
            
            let updatedChats;
            if (existingChatIndex !== -1) {
              // Update existing chat
              const existingChat = existingChats[existingChatIndex];
              existingChats[existingChatIndex] = {
                ...existingChat,
                lastMessage: `🎉 Booking Confirmed! ${lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer'} - ${selectedDate} at ${selectedTime}`,
                lastMessageTime: 'Just now',
                unreadCount: (existingChat.unreadCount || 0) + 1,
                bookingDate: selectedDate,
                bookingTime: selectedTime,
                consultationTypes: selectedConsultationTypes.map(type => type === 'call' ? 'Phone Call' : type === 'video' ? 'Video Call' : type === 'text' ? 'Text Consultation' : type === 'emergency' ? 'Emergency Consultation' : 'In Person Consultation'),
                profilePic: lawyer.profile_pic || lawyer.profilePic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
                lawyerName: lawyer.full_name || lawyer.fullName || lawyer.name || lawyer.lawyerName || 'Unknown Lawyer',
                specialization: Array.isArray(lawyer.specialization) ? lawyer.specialization.join(', ') : lawyer.specialization || 'General Law'
              };
              updatedChats = existingChats;
              toast.success('Booking added to existing chat!');
            } else {
              // Add new chat
              updatedChats = [...existingChats, newChat];
            }
            
            localStorage.setItem('userChats', JSON.stringify(updatedChats));
            console.log('💬 Chat created and added to localStorage:', newChat);
            console.log('📋 Updated chats list:', updatedChats);
            console.log('🎯 Lawyer info in chat:', newChat.lawyer);
            console.log('📅 Booking date and time:', { date: selectedDate, time: selectedTime });
            console.log('🔍 localStorage userChats after update:', JSON.parse(localStorage.getItem('userChats') || '[]'));

                         const successMessage = selectedPaymentMethod === 'wallet' 
               ? `Booking created successfully! ₹${(getTotalAmount() + 200).toLocaleString()} deducted from wallet. Waiting for lawyer approval.`
               : 'Booking created successfully! Waiting for lawyer approval.';
             toast.success(successMessage);
            
            // Dispatch custom event to notify other components about the booking
            window.dispatchEvent(new CustomEvent('bookingUpdated'));
            
            // Dispatch event to update chat list
            window.dispatchEvent(new CustomEvent('chatUpdated'));
            
            // Force refresh of FindLawyer component
            window.dispatchEvent(new CustomEvent('lawyersListRefresh'));
            
            setTimeout(() => {
              setShowBookingModal(false);
              navigate('/my-bookings');
            }, 2000);
          } else {
            console.error('❌ Chat creation failed:', chatResult.error);
            toast.error('Failed to create chat. Please try again.');
            
            // Continue with the rest of the booking process without creating a fallback chat
            const successMessage = selectedPaymentMethod === 'wallet' 
              ? `Booking created successfully! ₹${(getTotalAmount() + 200).toLocaleString()} deducted from wallet. Waiting for lawyer approval.`
              : 'Booking created successfully! Waiting for lawyer approval.';
            toast.success(successMessage);
            
            // Dispatch custom event to notify other components about the booking
            window.dispatchEvent(new CustomEvent('bookingUpdated'));
            window.dispatchEvent(new CustomEvent('chatUpdated'));
            window.dispatchEvent(new CustomEvent('lawyersListRefresh'));
            
            setTimeout(() => {
              setShowBookingModal(false);
              navigate('/my-bookings');
            }, 2000);
          }
        } else {
          toast.error('Error creating booking. Please try again.');
        }
      } catch (error) {
        console.error('Booking error:', error);
        toast.error('Booking failed. Please try again.');
      }
    } else if (step === 4) {
      setShowBookingModal(false);
      navigate('/my-bookings');
    } else {
      setBookingStep(step);
    }
  };

  const validateFirstStep = () => {
    console.log('=== VALIDATION DEBUG ===');
    console.log('Selected date:', selectedDate);
    console.log('Selected time:', selectedTime);
    console.log('Selected consultation types:', selectedConsultationTypes);
         console.log('Lawyer available days:', lawyer?.available_days);
    
    if (!selectedDate || !selectedTime || selectedConsultationTypes.length === 0) {
      toast.error('Please select a date, time, and at least one consultation type.');
      return false;
    }
    
         // Check if selected consultation types are valid (exist in available types)
     const availableTypes = [...consultationTypes.map(type => type.id), ...((lawyer?.emergency_available || lawyer?.emergencyConsultationAvailable) ? ['emergency'] : [])];
    const invalidTypes = selectedConsultationTypes.filter(type => !availableTypes.includes(type));
    
    if (invalidTypes.length > 0) {
      toast.error('Some selected consultation types are not available.');
      return false;
    }
    
    try {
      const selectedDateObj = new Date(selectedDate);
      
      // Check if the date is valid
      if (isNaN(selectedDateObj.getTime())) {
        console.log('Invalid date detected');
        toast.error('Please select a valid date.');
        return false;
      }
      
      // Check if the selected date is in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDateObj < today) {
        console.log('Past date detected');
        toast.error('Please select a future date.');
        return false;
      }
      
             // Get the day name in different formats
       const dayOfWeekCapitalized = selectedDateObj.toLocaleDateString('en-US', { weekday: 'long' });
       const dayOfWeekLower = dayOfWeekCapitalized.toLowerCase();
      
      console.log('Selected day (lowercase):', dayOfWeekLower);
      console.log('Selected day (capitalized):', dayOfWeekCapitalized);
      
             // Check if the selected date is an available day (only if available_days is set)
       if (lawyer && lawyer.available_days && Array.isArray(lawyer.available_days) && lawyer.available_days.length > 0) {
         console.log('Available days array:', lawyer.available_days);
         
         // Check if either format is available (case-insensitive)
         const isAvailable = lawyer.available_days.some(day => {
          const dayLower = day.toLowerCase();
          const matches = dayLower === dayOfWeekLower || dayLower === dayOfWeekCapitalized.toLowerCase();
          console.log(`Checking day "${day}" (${dayLower}) against "${dayOfWeekLower}" or "${dayOfWeekCapitalized.toLowerCase()}" = ${matches}`);
          return matches;
        });
        
        console.log('Is available:', isAvailable);
        
        if (!isAvailable) {
          toast.error('The selected date is not an available day for this lawyer.');
          return false;
        }
      } else {
        console.log('No available days validation - proceeding');
      }
      

      return true;
    } catch (error) {
      console.error('Error validating date:', error);
      toast.error('Please select a valid date.');
      return false;
    }
  };

  const resetSelections = () => {
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedConsultationTypes([]);
    setSelectedFileReadingCharges([]);
    setSelectedPaymentMethod(null);
  };

  const renderBookingModal = () => {
    if (!showBookingModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-2xl overflow-y-auto max-h-[80vh]"
        >
          {bookingStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Select Date, Time & Consultation Type</h3>

              {/* Date Picker */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Select Date</label>
                <input
                  type="date"
                  value={selectedDate || ''}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setSelectedTime(null); // Reset time when date changes
                  }}
                  min={new Date().toISOString().split('T')[0]} // Don't allow past dates
                  className="w-full p-4 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:border-purple-500 transition-colors duration-200"
                />
                                 {lawyer.available_days && lawyer.available_days.length > 0 && (
                   <div className="text-xs text-gray-500 dark:text-gray-400">
                     Available days: {lawyer.available_days.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')}
                   </div>
                 )}
                 {selectedDate && lawyer.available_days && lawyer.available_days.length > 0 && (
                   (() => {
                     const selectedDateObj = new Date(selectedDate);
                     const dayOfWeek = selectedDateObj.toLocaleDateString('en-US', { weekday: 'long' });
                     const isAvailable = lawyer.available_days.some(day => 
                      day.toLowerCase() === dayOfWeek.toLowerCase()
                    );
                    return !isAvailable ? (
                      <div className="text-xs text-red-500 dark:text-red-400">
                        ⚠️ {dayOfWeek} is not an available day. Please select a different date.
                      </div>
                    ) : null;
                  })()
                )}
              </div>

              {/* Time Slots */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Select Time</label>
                {availableTimeSlots.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {availableTimeSlots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => setSelectedTime(slot)}
                        className={`p-3 rounded-xl transition-colors duration-200 text-sm font-medium ${
                          selectedTime === slot 
                            ? 'bg-purple-600 text-white' 
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      {selectedDate ? 'No time slots available for this day' : 'Please select a date first'}
                    </p>
                  </div>
                )}
              </div>

                             {/* Consultation Type & Pricing */}
               <div className="space-y-3">
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Consultation Types (Select Multiple)</label>
                 {consultationTypes.length > 0 ? (
                   consultationTypes.map((type) => (
                   <button
                     key={type.id}
                     onClick={() => handleConsultationTypeToggle(type.id)}
                     className={`w-full flex items-center justify-between p-4 rounded-xl transition-colors duration-200 ${
                       selectedConsultationTypes.includes(type.id)
                         ? 'bg-purple-600 text-white' 
                         : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                     }`}
                   >
                     <div className="flex items-center gap-3">
                       {type.icon}
                       <span className="font-medium">{type.name}</span>
                     </div>
                     <div className="flex items-center gap-2">
                       <span className="font-bold text-purple-500">{type.price}</span>
                       {selectedConsultationTypes.includes(type.id) && (
                         <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
                           <span className="text-purple-600 text-xs">✓</span>
                         </div>
                       )}
                     </div>
                   </button>
                 ))
                 ) : (
                   <div className="text-center py-4">
                     <p className="text-gray-500 dark:text-gray-400 text-sm">
                       No consultation types configured for this lawyer
                     </p>
                   </div>
                 )}
               </div>

                               {/* Emergency Consultation */}
                {(lawyer.emergency_available || lawyer.emergencyConsultationAvailable) && (
                  <div className="space-y-3">
                    <button
                      key="emergency"
                      onClick={() => handleConsultationTypeToggle('emergency')}
                      className={`w-full flex items-center justify-between p-4 rounded-xl transition-colors duration-200 ${
                        selectedConsultationTypes.includes('emergency')
                          ? 'bg-red-600 text-white' 
                          : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Zap className="w-5 h-5 text-red-600" />
                        <div className="text-left">
                          <span className="font-medium">Emergency Consultation</span>
                          <p className="text-xs text-gray-800 dark:text-gray-400">All services included • Instant reply within 1hr</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                                                 <span className="font-bold text-red-600">₹{lawyer.fees?.emergency || lawyer.emergencyConsultationFee}</span>
                        {selectedConsultationTypes.includes('emergency') && (
                          <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
                            <span className="text-red-600 text-xs">✓</span>
                          </div>
                        )}
                      </div>
                    </button>
                  </div>
                )}

                             {/* File Reading Charges */}
               {lawyer.file_charges && lawyer.file_charges.length > 0 ? (
                 <div className="space-y-3">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">File Reading Charges (Select Multiple)</label>
                   <div className="space-y-2">
                     {lawyer.file_charges.map((charge, index) => (
                       <button
                         key={index}
                         onClick={() => handleFileReadingChargeToggle(charge)}
                         className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors duration-200 text-sm ${
                           selectedFileReadingCharges.find(c => c.fromPage === charge.fromPage)
                             ? 'bg-purple-600 text-white' 
                             : 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600'
                         }`}
                       >
                         <span className="font-medium">
                           {charge.fromPage} - {charge.toPage} pages
                         </span>
                         <div className="flex items-center gap-2">
                           <span className="font-bold">₹{charge.amount}</span>
                           {selectedFileReadingCharges.find(c => c.fromPage === charge.fromPage) && (
                             <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                               <span className="text-purple-600 text-xs">✓</span>
                             </div>
                           )}
                         </div>
                       </button>
                     ))}
                   </div>
                 </div>
               ) : (
                 <div className="space-y-3">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">File Reading Charges</label>
                   <div className="text-center py-4">
                     <p className="text-gray-500 dark:text-gray-400 text-sm">
                       No file reading charges configured for this lawyer
                     </p>
                   </div>
                 </div>
               )}

              {/* Navigation Buttons */}
              <div className="flex gap-3 pt-4">
                                 <button
                   onClick={() => {
                     setShowBookingModal(false);
                     resetSelections();
                   }}
                   className="flex-1 py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                 >
                   {t.cancel}
                 </button>
                <button
                  onClick={() => {
                    if (validateFirstStep()) handleBookingStep(2);
                  }}
                  className="flex-1 py-3 px-4 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors duration-200"
                >
                  {t.next}
                </button>
              </div>
            </div>
          )}

                     {/* Payment Method Modal */}
           {bookingStep === 2 && (
             <div className="space-y-4">
               <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t.paymentMethod}</h3>
               
               {/* Wallet Balance Display */}
               <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
                 <div className="flex items-center justify-between">
                   <div className="flex items-center gap-3">
                     <Wallet className="w-6 h-6 text-purple-600" />
                     <div>
                       <p className="text-sm text-gray-600 dark:text-gray-400">Current Wallet Balance</p>
                       <p className="text-xl font-bold text-purple-600">₹{walletBalance.toLocaleString()}</p>
                     </div>
                   </div>
                   <button
                     onClick={refreshWalletBalance}
                     className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                     title="Refresh balance"
                   >
                     <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                     </svg>
                   </button>
                 </div>
                 {walletBalance < (getTotalAmount() + 200) && (
                   <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                     <p className="text-xs text-red-600 dark:text-red-400 mb-2">
                       ⚠️ Insufficient balance. Add ₹{((getTotalAmount() + 200) - walletBalance).toLocaleString()} more to book.
                     </p>
                     <div className="flex gap-2">
                       <button
                         onClick={() => addMoneyToWallet(500)}
                         className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                       >
                         +₹500
                       </button>
                       <button
                         onClick={() => addMoneyToWallet(1000)}
                         className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                       >
                         +₹1000
                       </button>
                       <button
                         onClick={() => addMoneyToWallet(2000)}
                         className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                       >
                         +₹2000
                       </button>
                     </div>
                   </div>
                 )}
                 
                 {/* Quick Add Money Buttons */}
                 <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-800">
                   <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Quick Add Money:</p>
                   <div className="flex gap-2">
                     <button
                       onClick={() => addMoneyToWallet(500)}
                       className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-xs hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                     >
                       +₹500
                     </button>
                     <button
                       onClick={() => addMoneyToWallet(1000)}
                       className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-xs hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                     >
                       +₹1000
                     </button>
                     <button
                       onClick={() => addMoneyToWallet(2000)}
                       className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-xs hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                     >
                       +₹2000
                     </button>
                   </div>
                 </div>
               </div>
                             <div className="space-y-3">
                 {paymentMethods.map((method) => (
                   <button
                     key={method.id}
                     onClick={() => setSelectedPaymentMethod(method.id)}
                     className={`w-full flex items-center gap-3 p-4 rounded-xl transition-colors duration-200 ${
                       selectedPaymentMethod === method.id
                         ? 'bg-purple-600 text-white border-2 border-purple-600'
                         : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 border-2 border-transparent'
                     }`}
                   >
                     {method.icon}
                     <span className="font-medium">{method.name}</span>
                     {method.extra && method.extra}
                     {selectedPaymentMethod === method.id && !method.extra && (
                       <div className="ml-auto w-5 h-5 bg-white rounded-full flex items-center justify-center">
                         <span className="text-purple-600 text-xs">✓</span>
                       </div>
                     )}
                     {selectedPaymentMethod === method.id && method.extra && (
                       <div className="ml-auto w-5 h-5 bg-white rounded-full flex items-center justify-center">
                         <span className="text-purple-600 text-xs">✓</span>
                       </div>
                     )}
                   </button>
                 ))}
               </div>
                             <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-4 space-y-2">
                 {/* Consultation Fees Breakdown */}
                 {selectedConsultationTypes.length > 0 && (
                   <div className="space-y-1">
                     <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Consultation Services:</div>
                     {selectedConsultationTypes.map((type, index) => (
                       <div key={index} className="flex justify-between text-sm">
                         <span className="text-gray-600 dark:text-gray-300">
                           {type === 'call' ? 'Phone Call' : type === 'video' ? 'Video Call' : type === 'text' ? 'Text Consultation' : type === 'emergency' ? 'Emergency Consultation' : 'In Person Consultation'}
                         </span>
                         <span className="font-medium text-gray-900 dark:text-white">₹{getConsultationFee(type)}</span>
                       </div>
                     ))}
                     <div className="flex justify-between pt-1 border-t border-gray-300 dark:border-gray-600">
                       <span className="text-gray-600 dark:text-gray-300">Total Consultation Fee</span>
                       <span className="font-medium text-gray-900 dark:text-white">₹{getTotalConsultationFees().toLocaleString()}</span>
                     </div>
                   </div>
                 )}
                 
                 {/* File Reading Charges Breakdown */}
                 {selectedFileReadingCharges.length > 0 && (
                   <div className="space-y-1 pt-2 border-t border-gray-300 dark:border-gray-600">
                     <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">File Reading Services:</div>
                     {selectedFileReadingCharges.map((charge, index) => (
                       <div key={index} className="flex justify-between text-sm">
                         <span className="text-gray-600 dark:text-gray-300">
                           {charge.fromPage} - {charge.toPage} pages
                         </span>
                         <span className="font-medium text-gray-900 dark:text-white">₹{charge.amount}</span>
                       </div>
                     ))}
                     <div className="flex justify-between pt-1 border-t border-gray-300 dark:border-gray-600">
                       <span className="text-gray-600 dark:text-gray-300">Total File Reading</span>
                       <span className="font-medium text-gray-900 dark:text-white">₹{getTotalFileReadingCharges().toLocaleString()}</span>
                     </div>
                   </div>
                 )}
                 
                 <div className="flex justify-between">
                   <span className="text-gray-600 dark:text-gray-300">{t.serviceCharge}</span>
                   <span className="font-medium text-gray-900 dark:text-white">₹200</span>
                 </div>
                 <div className="flex justify-between pt-2 border-t border-gray-300 dark:border-gray-600">
                   <span className="font-bold text-gray-900 dark:text-white">{t.total}</span>
                   <span className="font-bold text-purple-600">₹{(getTotalAmount() + 200).toLocaleString()}</span>
                 </div>
               </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setBookingStep(1)}
                  className="flex-1 py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  Back
                </button>
                                 <button
                   onClick={() => {
                     if (!selectedPaymentMethod) {
                       toast.error('Please select a payment method');
                       return;
                     }
                     
                     // Check wallet balance if wallet payment is selected
                     if (selectedPaymentMethod === 'wallet') {
                       const totalAmount = getTotalAmount() + 200;
                       if (walletBalance < totalAmount) {
                         toast.error(`Insufficient wallet balance. Required: ₹${totalAmount.toLocaleString()}, Available: ₹${walletBalance.toLocaleString()}`);
                         return;
                       }
                     }
                     
                     handleBookingStep(3);
                   }}
                   className="flex-1 py-3 px-4 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors duration-200"
                 >
                   {t.payAndBook}
                 </button>
              </div>
            </div>
          )}

          {/* Confirmation Modal */}
          {bookingStep === 3 && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t.confirmation}</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">{t.confirmationMessage}</p>
              </div>
              <button
                onClick={() => handleBookingStep(4)}
                className="w-full py-3 px-4 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors duration-200"
              >
                {t.backToHome}
              </button>
            </div>
          )}
        </motion.div>
      </div>
    );
  };

  if (isLoading || !lawyer) {
    return (
      <div className="app-screen-container" style={{ minHeight: '100vh', paddingTop: '0px', paddingBottom: '0px' }}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading lawyer profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">



      {/* Header */}
      <div className="app-header" style={{ flex: '0 0 auto', padding: '10px' }}>
        <div className="header-content">
          <button
            onClick={handleBack}
            className="flex items-center justify-center p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
            aria-label="Go back"
          >
            <ArrowLeft className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </button>
                     <div className="flex-1 text-center pr-12">
             <div className="flex items-center justify-center gap-2 mb-1">
               <h1 className="header-title">{lawyer.name || 'Lawyer Profile'}</h1>
               <div className={`w-2 h-2 rounded-full ${lawyer.isOnline || lawyer.availability === 'online' ? 'bg-green-500' : 'bg-red-500'}`}></div>
             </div>
             <p className="text-sm text-text-muted font-medium">
               {Array.isArray(lawyer.specialization) ? lawyer.specialization.join(', ') : lawyer.specialization || 'General Law'} • {lawyer.bar_council_state || lawyer.location || 'Location not specified'}
             </p>
                           <div className="flex items-center justify-center gap-2 mt-1">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  lawyer.isOnline || lawyer.availability === 'online' 
                    ? 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30' 
                    : 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30'
                }`}>
                  {lawyer.isOnline || lawyer.availability === 'online' ? '🟢 Online' : '🔴 Offline'}
                </span>
                {lawyer.isVerified && (
                  <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                    ✓ Verified
                  </span>
                )}
                                 
              </div>
           </div>
        </div>
      </div>

             {/* Content */}
       <div className="container mobile-padding space-y-4" style={{ flexGrow: 1, padding: '10px', paddingBottom: '100px' }}>
                   {/* Multiple bookings allowed - no booking status notice */}
         
         <motion.div
           initial={{ opacity: 0, y: 20 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ delay: 0.1 }}
           className="space-y-4"
         >
          {/* Profile Picture */}
          <div className="text-center">
                         {lawyer.profile_pic || lawyer.profilePic ? (
               <motion.img
                 initial={{ opacity: 0, scale: 0.8 }}
                 animate={{ opacity: 1, scale: 1 }}
                 transition={{ delay: 0.2 }}
                 src={lawyer.profile_pic || lawyer.profilePic}
                 alt={lawyer.name}
                 className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-purple-200 dark:border-purple-800 shadow-lg"
               />
             ) : (
               <motion.div
                 initial={{ opacity: 0, scale: 0.8 }}
                 animate={{ opacity: 1, scale: 1 }}
                 transition={{ delay: 0.2 }}
                 className="w-32 h-32 rounded-full bg-purple-100 dark:bg-purple-900/30 border-4 border-purple-200 dark:border-purple-800 shadow-lg flex items-center justify-center mx-auto"
               >
                 <span className="text-4xl text-purple-600 dark:text-purple-400">👨‍💼</span>
               </motion.div>
             )}
            <div className="flex items-center justify-center gap-1 mt-3">
              <Star className="w-5 h-5 text-yellow-500 fill-current" />
              <span className="font-semibold text-gray-900 dark:text-white">{lawyer.rating}</span>
              <span className="text-gray-500 dark:text-gray-400">({lawyer.totalReviews || lawyer.reviews || 0} reviews)</span>
            </div>
          </div>

          {/* Stats Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-600" />
              {t.stats}
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg mb-2">
                  <Shield className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto" />
                </div>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">95%</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t.successRate}</p>
              </div>
              <div className="text-center">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg mb-2">
                  <Star className="w-6 h-6 text-blue-600 dark:text-blue-400 mx-auto" />
                </div>
                <p className="text-lg font-bold text-blue-600 dark:text-blue-400">92%</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t.accuracyScore}</p>
              </div>
              <div className="text-center">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg mb-2">
                  <Calendar className="w-6 h-6 text-purple-600 dark:text-purple-400 mx-auto" />
                </div>
                <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{lawyer.experience_years || lawyer.experience || lawyer.yearsExperience || 0}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Years</p>
              </div>
            </div>
          </motion.div>

          {/* Contact Information Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
                         <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
               <Users className="w-5 h-5 text-purple-600" />
               {t.contactInfo}
                               
             </h3>
                                                   <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-300">{lawyer.bar_council_state || lawyer.location}</span>
                </div>
               {(lawyer.chamberAddress || lawyer.chamber_address || lawyer.address || lawyer.lawyer?.chamberAddress) && (
                 <div className="flex items-center gap-3">
                   <Building2 className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                   <span className="text-gray-600 dark:text-gray-300 text-sm">{lawyer.chamberAddress || lawyer.chamber_address || lawyer.address || lawyer.lawyer?.chamberAddress || 'Chamber address not specified'}</span>
                 </div>
               )}
               <div className="flex items-center gap-3">
                 <Scale className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                 <span className="text-gray-600 dark:text-gray-300">{t.barLicense}: {lawyer.bar_council_number || lawyer.barCouncilLicense || lawyer.barLicense || lawyer.lawyer?.barLicense || 'Not specified'}</span>
               </div>
                              <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-300">{t.consultationFee}: ₹{lawyer.fees?.consultation || lawyer.fee || lawyer.consultationFee || lawyer.lawyer?.consultationFees || lawyer.lawyer?.fees?.consultation || 2500}</span>
                </div>
               
               {/* Working Hours */}
               {(lawyer.workingHours || lawyer.working_hours) && Object.keys(lawyer.workingHours || lawyer.working_hours || {}).length > 0 && (
                 <div className="space-y-2">
                   <div className="flex items-center gap-3">
                     <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                     <span className="text-gray-600 dark:text-gray-300 font-medium">Working Hours:</span>
                   </div>
                   <div className="ml-8 space-y-1">
                     {Object.entries(lawyer.workingHours || lawyer.working_hours || {}).map(([day, hours]) => (
                       <div key={day} className="flex justify-between text-sm">
                         <span className="text-gray-600 dark:text-gray-300 capitalize">{day}:</span>
                         <span className="text-gray-900 dark:text-white font-medium">
                           {hours.start} - {hours.end}
                         </span>
                       </div>
                     ))}
                   </div>
                 </div>
               )}
               
                               {/* Available Days */}
                {lawyer.available_days && lawyer.available_days.length > 0 && (
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    <div className="flex flex-wrap gap-1">
                      <span className="text-gray-600 dark:text-gray-300 text-sm">Available:</span>
                      {lawyer.available_days.map((day, index) => (
                       <span key={index} className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full">
                         {day}
                       </span>
                     ))}
                   </div>
                 </div>
               )}
                                                           {lawyer.emergencyConsultationAvailable && (
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-red-500" />
                    <span className="text-gray-600 dark:text-gray-300">Emergency Consultation: ₹{lawyer.fees?.emergency || lawyer.emergencyConsultationFee}</span>
                  </div>
                )}
              
              {/* Social Media Links */}
              <div className="flex items-center gap-3 pt-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Connect:</span>
                <div className="flex gap-2">
                  {/* Email - always show if available */}
                  {((lawyer.social?.email && lawyer.social.email.trim() !== '') || (lawyer.gmail && lawyer.gmail.trim() !== '')) && (
                    <a href={`mailto:${lawyer.social?.email || lawyer.gmail}`} className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors">
                      <Mail className="w-4 h-4 text-purple-600" />
                    </a>
                  )}
                  {/* LinkedIn - only show if not empty */}
                  {lawyer.social?.linkedin && lawyer.social.linkedin.trim() !== '' && (
                    <a href={lawyer.social.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors">
                      <Linkedin className="w-4 h-4 text-purple-600" />
                    </a>
                  )}
                  {/* Facebook - only show if not empty */}
                  {lawyer.social?.facebook && lawyer.social.facebook.trim() !== '' && (
                    <a href={lawyer.social.facebook} target="_blank" rel="noopener noreferrer" className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors">
                      <Facebook className="w-4 h-4 text-purple-600" />
                    </a>
                  )}
                  {/* Twitter - only show if not empty */}
                  {lawyer.social?.twitter && lawyer.social.twitter.trim() !== '' && (
                    <a href={lawyer.social.twitter} target="_blank" rel="noopener noreferrer" className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors">
                      <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                      </svg>
                    </a>
                  )}
                  {/* Instagram - only show if not empty */}
                  {lawyer.social?.instagram && lawyer.social.instagram.trim() !== '' && (
                    <a href={lawyer.social.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors">
                      <Instagram className="w-4 h-4 text-purple-600" />
                    </a>
                  )}
                  {/* Website - only show if not empty */}
                  {lawyer.social?.website && lawyer.social.website.trim() !== '' && (
                    <a href={lawyer.social.website} target="_blank" rel="noopener noreferrer" className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                      </svg>
                    </a>
                  )}
                  {/* WhatsApp - only show if not empty */}
                  {lawyer.social?.whatsapp && lawyer.social.whatsapp.trim() !== '' && (
                    <a href={`https://wa.me/${lawyer.social.whatsapp}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors">
                      <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            </div>
                                                                                                                                                                                                           {isLawyerBooked ? (
                  <div className="w-full mt-4 space-y-3">
                    <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800 dark:text-green-200">Already Booked</span>
                      </div>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        You have an existing booking with this lawyer. Check your bookings for details.
                      </p>
            </div>
                    <button
                      onClick={() => navigate('/my-bookings')}
                      className="w-full py-3 px-4 rounded-xl transition-all duration-200 transform font-medium bg-green-600 text-white hover:bg-green-700 hover:scale-105"
                    >
                      View My Bookings
                    </button>
                  </div>
                ) : (
                                                                                                                                                                                                           <button
                  onClick={handleBookConsultation}
                  className="w-full mt-4 py-3 px-4 rounded-xl transition-all duration-200 transform font-medium bg-purple-600 text-white hover:bg-purple-700 hover:scale-105"
                >
                  {t.bookConsultation}
                </button>
                )}
             
             
             

          </motion.div>

          {/* Professional Bio Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
                         <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t.professionalBio}</h3>
             <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{lawyer.bio || 'Professional bio not available'}</p>
            
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-purple-600" />
                  {t.education}
                </h4>
                                 {lawyer.education && (Array.isArray(lawyer.education) || typeof lawyer.education === 'object') ? (
                   <div className="space-y-3">
                     {(() => {
                       // Handle both array and object formats
                       let educationArray = [];
                       if (Array.isArray(lawyer.education)) {
                         educationArray = lawyer.education;
                       } else if (typeof lawyer.education === 'object') {
                         // Convert object with numeric keys to array
                         educationArray = Object.values(lawyer.education);
                       }
                       
                       return educationArray.map((edu, index) => (
                         <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                           <div className="flex items-center gap-2 mb-1">
                             <GraduationCap className="w-4 h-4 text-purple-600" />
                             <p className="font-semibold text-gray-900 dark:text-white">
                               {edu.q || edu.qualification || edu.degree || 'Law Degree'}
                             </p>
                           </div>
                           {edu.c && (
                             <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                               <span className="font-medium">Institution:</span> {edu.c}
                             </p>
                           )}
                           {edu.y && edu.y.trim() !== '' && (
                             <p className="text-xs text-gray-500 dark:text-gray-400">
                               <span className="font-medium">Year:</span> {edu.y}
                             </p>
                           )}
                         </div>
                       ));
                     })()}
                   </div>
                ) : lawyer.degree ? (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center gap-2 mb-1">
                      <GraduationCap className="w-4 h-4 text-purple-600" />
                      <p className="font-semibold text-gray-900 dark:text-white">{lawyer.degree}</p>
                    </div>
                    {lawyer.yearOfPassing && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="font-medium">Year of Passing:</span> {lawyer.yearOfPassing}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-gray-400" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Education information not available</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">{t.specializations}</h4>
                <div className="flex flex-wrap gap-2">
                  {lawyer.specialization && (
                    Array.isArray(lawyer.specialization) 
                      ? lawyer.specialization.map((spec, index) => (
                          <span key={index} className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs">
                            {spec}
                          </span>
                        ))
                      : lawyer.specialization.split(', ').map((spec, index) => (
                    <span key={index} className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs">
                      {spec}
                    </span>
                        ))
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">{t.languages}</h4>
                <div className="flex flex-wrap gap-2">
                  {lawyer.languages && (
                    Array.isArray(lawyer.languages) 
                      ? lawyer.languages.map((lang, index) => (
                          <span key={index} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs">
                            {lang}
                          </span>
                        ))
                      : lawyer.languages.split(', ').map((lang, index) => (
                    <span key={index} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs">
                      {lang}
                    </span>
                        ))
                  )}
                </div>
              </div>
              
              {lawyer.achievements && lawyer.achievements.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Achievements
                  </h4>
                  <div className="space-y-2">
                    {lawyer.achievements.map((achievement, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                        <span>{typeof achievement === 'string' ? achievement : `${achievement.name || achievement} (${achievement.year || 'N/A'})`}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Experience Management Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-purple-600" />
              Experience Management
            </h3>
            
            {lawyer.experiences && lawyer.experiences.length > 0 ? (
              <div className="space-y-4">
                {lawyer.experiences.map((exp, index) => (
                  <div key={exp.id || index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Experience {index + 1}
                      </h4>
                      <span className="text-sm text-purple-600 font-medium">
                        {exp.yearsOfPractice} years
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      {exp.subPracticeArea && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sub Practice Area:</span>
                          <span className="text-sm text-gray-900 dark:text-white">{exp.subPracticeArea}</span>
                        </div>
                      )}
                      
                      {exp.casesHandled && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Cases Handled:</span>
                          <span className="text-sm text-gray-900 dark:text-white">{exp.casesHandled} cases</span>
                        </div>
                      )}
                      
                      {exp.yearsOfPractice && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Years of Practice:</span>
                          <span className="text-sm text-gray-900 dark:text-white">{exp.yearsOfPractice} years</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">💼</div>
                <p className="text-gray-500 dark:text-gray-400">No detailed experience information available</p>
              </div>
            )}
          </motion.div>



          


        </motion.div>
      </div>

      {/* Booking Modal */}
      {renderBookingModal()}
    </div>
  );
};

export default LawyerDetail;